import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Template from "../app/Template";
import Container from "../layout/Container";
import Card from "../layout/Card";
import SimpleButton from "./SimpleButton";
import TextArea from "../forms/TextArea";
import AlertModal from "./AlertModal";
import ConfirmModal from "./ConfirmModal";
import RejectModal from "./RejectModal";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

interface ReviewData {
  content_item: {
    content_id: string;
    title: string;
    author: string;
    content_type: string;
    status: string;
    transcript: string;
    created_at: string;
  };
  tagging: {
    original_tags: any;
    reviewed_tags: any | null;
    status: string;
  } | null;
  filtering: {
    original_result: any;
    reviewed_result: any | null;
    final_result?: any;
    status: string;
  } | null;
  linkage: {
    transcript_hash: string;
    hashes_match: boolean;
  };
  /** Present when GET review includes production Post lookup (same URL as loader). */
  production?: {
    loaded_post_id: string | null;
    post_approved_for_site: boolean | null;
    public_site_visible: boolean | null;
    visibility_note: string | null;
    canonical_url?: string;
  };
}

/** Effective filtering payload: API final_result, else reviewed, else original. */
function getFilteringFinal(
  filtering: ReviewData["filtering"]
): Record<string, unknown> | null {
  if (!filtering) return null;
  const raw =
    filtering.final_result ?? filtering.reviewed_result ?? filtering.original_result;
  return raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
}

type PipelineViewMode = "review" | "details";

interface ReviewInterfaceProps {
  mode: PipelineViewMode;
}

export default function ReviewInterface({ mode }: ReviewInterfaceProps) {
  const { contentId } = useParams<{ contentId: string }>();
  const navigate = useNavigate();
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);

  // Edited values
  const [editedPrimaryTags, setEditedPrimaryTags] = useState<string[]>([]);
  const [editedSecondaryTags, setEditedSecondaryTags] = useState<string[]>([]);
  const [reviewerNotes, setReviewerNotes] = useState("");

  const [filterEditMode, setFilterEditMode] = useState(false);
  const [filterFlags, setFilterFlags] = useState({
    generalization: false,
    othering: false,
    lack_of_direct_representation: false,
  });
  const [filteringNotes, setFilteringNotes] = useState("");

  // Modal states
  const [alertModal, setAlertModal] = useState({ show: false, title: "", message: "", type: "info" as "success" | "error" | "warning" | "info" });
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    fetchReviewData();
  }, [contentId]);

  /** Review URL is only for actionable review/publish; terminal states use read-only details. */
  useEffect(() => {
    if (mode !== "review" || !reviewData?.content_item?.status || !contentId) return;
    const s = reviewData.content_item.status;
    if (s === "rejected" || s === "loaded" || s === "failed") {
      navigate(`/pipeline/details/${contentId}`, { replace: true });
    }
  }, [mode, reviewData?.content_item?.status, contentId, navigate]);

  useEffect(() => {
    if (mode === "details") {
      setEditMode(false);
      setFilterEditMode(false);
    }
  }, [mode, contentId]);

  const fetchReviewData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access');
      const response = await fetch(`/api/pipeline/processing/review/${contentId}/`, {
        headers: {
          ...(token && { "Authorization": `Bearer ${token}` }),
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setReviewData(data);
        
        // Initialize edit values
        if (data.tagging) {
          const tags = data.tagging.reviewed_tags || data.tagging.original_tags;
          setEditedPrimaryTags(tags.primary_tags || []);
          setEditedSecondaryTags(tags.secondary_tags || []);
        }
        const ff = data.filtering ? getFilteringFinal(data.filtering) : null;
        const summary = ff?.summary as Record<string, unknown> | undefined;
        const ctx = summary?.context_analysis as Record<string, unknown> | undefined;
        const analysis = ctx?.analysis as Record<string, unknown> | undefined;
        if (analysis) {
          setFilterFlags({
            generalization: !!analysis.generalization,
            othering: !!analysis.othering,
            lack_of_direct_representation: !!analysis.lack_of_direct_representation,
          });
        } else {
          setFilterFlags({
            generalization: false,
            othering: false,
            lack_of_direct_representation: false,
          });
        }
        setFilterEditMode(false);
        setFilteringNotes("");
      } else {
        setAlertModal({
          show: true,
          title: "Load Failed",
          message: "Failed to load review data. Redirecting to dashboard...",
          type: "error"
        });
        setTimeout(() => navigate("/pipeline/dashboard"), 2000);
      }
    } catch (error) {
      console.error("Error fetching review data:", error);
      setAlertModal({
        show: true,
        title: "Network Error",
        message: "Unable to connect to the server. Please check your connection.",
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTags = async () => {
    try {
      const token = localStorage.getItem('access');
      const response = await fetch("/api/pipeline/processing/review/edit-tags/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` }),
        },
        body: JSON.stringify({
          content_id: contentId,
          reviewed_tags: {
            primary_tags: editedPrimaryTags,
            secondary_tags: editedSecondaryTags,
          },
          reviewer_notes: reviewerNotes,
        }),
      });

      if (response.ok) {
        setAlertModal({
          show: true,
          title: "Tags Saved",
          message: "Your tag edits have been saved successfully!",
          type: "success"
        });
        setEditMode(false);
        fetchReviewData();
      } else {
        setAlertModal({
          show: true,
          title: "Save Failed",
          message: "Failed to save tags. Please try again.",
          type: "error"
        });
      }
    } catch (error) {
      console.error("Error saving tags:", error);
      setAlertModal({
        show: true,
        title: "Network Error",
        message: "Unable to save changes. Please check your connection.",
        type: "error"
      });
    }
  };

  const resetFilterEditFromData = () => {
    const ff = reviewData?.filtering ? getFilteringFinal(reviewData.filtering) : null;
    const summary = ff?.summary as Record<string, unknown> | undefined;
    const ctx = summary?.context_analysis as Record<string, unknown> | undefined;
    const analysis = ctx?.analysis as Record<string, unknown> | undefined;
    if (analysis) {
      setFilterFlags({
        generalization: !!analysis.generalization,
        othering: !!analysis.othering,
        lack_of_direct_representation: !!analysis.lack_of_direct_representation,
      });
    }
    setFilteringNotes("");
    setFilterEditMode(false);
  };

  const handleSaveFiltering = async () => {
    if (!contentId || !reviewData?.filtering) return;
    const base = getFilteringFinal(reviewData.filtering);
    if (!base) return;
    const merged = JSON.parse(JSON.stringify(base)) as Record<string, unknown>;
    if (!merged.summary || typeof merged.summary !== "object") merged.summary = {};
    const s = merged.summary as Record<string, unknown>;
    if (!s.context_analysis || typeof s.context_analysis !== "object") s.context_analysis = {};
    const ca = s.context_analysis as Record<string, unknown>;
    if (!ca.analysis || typeof ca.analysis !== "object") ca.analysis = {};
    const a = ca.analysis as Record<string, unknown>;
    a.generalization = filterFlags.generalization;
    a.othering = filterFlags.othering;
    a.lack_of_direct_representation = filterFlags.lack_of_direct_representation;

    try {
      const token = localStorage.getItem("access");
      const response = await fetch("/api/pipeline/processing/review/edit-filtering/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          content_id: contentId,
          reviewed_result: merged,
          reviewer_notes: filteringNotes,
        }),
      });

      if (response.ok) {
        setAlertModal({
          show: true,
          title: "Filtering Saved",
          message: "Context flag overrides have been saved.",
          type: "success",
        });
        setFilterEditMode(false);
        fetchReviewData();
      } else {
        setAlertModal({
          show: true,
          title: "Save Failed",
          message: "Could not save filtering overrides. Please try again.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error saving filtering:", error);
      setAlertModal({
        show: true,
        title: "Network Error",
        message: "Unable to save filtering changes.",
        type: "error",
      });
    }
  };

  const handleApprove = async () => {
    try {
      const token = localStorage.getItem('access');
      const response = await fetch("/api/pipeline/processing/review/approve/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` }),
        },
        body: JSON.stringify({
          content_id: contentId,
          approve_tags: true,
          approve_filtering: true,
          auto_load: false,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setShowApproveConfirm(false);
        setAlertModal({
          show: true,
          title: "Content Approved",
          message:
            "Review decision saved. Use “Publish to site” to create the live post (or publish from the dashboard).",
          type: "success",
        });
        await fetchReviewData();
      } else {
        setAlertModal({
          show: true,
          title: "Approval Failed",
          message: data.message || "Failed to approve content. Please try again.",
          type: "error"
        });
      }
    } catch (error) {
      console.error("Error approving content:", error);
      setAlertModal({
        show: true,
        title: "Network Error",
        message: "Unable to approve content. Please check your connection.",
        type: "error"
      });
    }
  };

  const handleReject = async (reason: string) => {
    try {
      const token = localStorage.getItem('access');
      const response = await fetch("/api/pipeline/processing/review/reject/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` }),
        },
        body: JSON.stringify({
          content_id: contentId,
          rejection_reason: reason,
        }),
      });

      if (response.ok) {
        setAlertModal({
          show: true,
          title: "Content Rejected",
          message: "Content has been rejected and removed from the pipeline.",
          type: "warning"
        });
        setTimeout(() => navigate("/pipeline/dashboard"), 1500);
      } else {
        setAlertModal({
          show: true,
          title: "Rejection Failed",
          message: "Failed to reject content. Please try again.",
          type: "error"
        });
      }
    } catch (error) {
      console.error("Error rejecting content:", error);
      setAlertModal({
        show: true,
        title: "Network Error",
        message: "Unable to reject content. Please check your connection.",
        type: "error"
      });
    }
  };

  const handlePublish = async () => {
    try {
      const token = localStorage.getItem("access");
      const response = await fetch("/api/pipeline/processing/review/load/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ content_id: contentId }),
      });
      const data = await response.json().catch(() => ({}));
      // Backend logs go to Docker (gunicorn), not DevTools — log API result here.
      // DevTools collapses long strings inside objects; log a flat summary too.
      console.info("[pipeline publish] /review/load/ response", data);
      console.info("[pipeline publish] summary", {
        loaded_post_id: data.loaded_post_id,
        public_site_visible: data.public_site_visible,
        post_approved_for_site: data.post_approved_for_site,
      });
      if (response.ok && data.success !== false) {
        const parts = [data.message || "Loaded to production.", data.visibility_note].filter(
          Boolean
        );
        setAlertModal({
          show: true,
          title: data.public_site_visible === false ? "Loaded (not public)" : "Published",
          message: parts.join(" "),
          type: "success",
        });
        await fetchReviewData();
        setTimeout(() => navigate("/pipeline/dashboard"), 1800);
      } else {
        setAlertModal({
          show: true,
          title: "Publish Failed",
          message: data.error || data.message || "Could not load content to production.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error publishing content:", error);
      setAlertModal({
        show: true,
        title: "Network Error",
        message: "Unable to publish. Please check your connection.",
        type: "error",
      });
    }
  };

  if (loading) {
    return (
      <Template navbarMode="solid">
        <Container className="mt-24 mb-20">
          <div className="text-center py-20">
            <p className="text-dark-grey text-xl">Loading review data...</p>
          </div>
        </Container>
      </Template>
    );
  }

  if (!reviewData) {
    return (
      <Template navbarMode="solid">
        <Container className="mt-24 mb-20">
          <Card colour="white" className="text-center" paddingY="py-20">
            <p className="text-dark-grey text-xl">Review data not found</p>
            <SimpleButton
              text="Back to Dashboard"
              colour="orange"
              onClick={() => navigate("/pipeline/dashboard")}
              className="mt-4"
            />
          </Card>
        </Container>
      </Template>
    );
  }

  const filteringFinal = getFilteringFinal(reviewData.filtering);
  const hasFilteringData = !!filteringFinal;
  const hasTaggingData = reviewData.tagging && reviewData.tagging.original_tags;
  const filteringAnalysis = filteringFinal?.summary as Record<string, unknown> | null | undefined;

  const itemStatus = reviewData.content_item.status;
  const statusBadgeLabel =
    itemStatus === "loaded"
      ? "PUBLISHED"
      : itemStatus.replace(/_/g, " ").toUpperCase();
  const isDetailsView = mode === "details";
  const showReviewActions =
    !isDetailsView &&
    mode === "review" &&
    itemStatus === "review_pending" &&
    hasFilteringData &&
    hasTaggingData;
  const showPublishActions =
    !isDetailsView && mode === "review" && itemStatus === "approved";

  return (
    <Template navbarMode="solid">
      <Container className="mt-20 sm:mt-24 md:mt-32 mb-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Card colour="yellow" className="mb-5" paddingX="px-6 sm:px-8" paddingY="py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-dark-grey text-2xl sm:text-3xl md:text-4xl font-bold mb-1">
                {isDetailsView ? "Content details" : "Review content"}
              </h1>
              <p className="text-dark-grey text-sm sm:text-base mb-2">
                <span
                  className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                    itemStatus === "review_pending"
                      ? "bg-purple-100 text-purple-900"
                      : itemStatus === "approved"
                        ? "bg-green-100 text-green-900"
                        : itemStatus === "loaded"
                          ? "bg-emerald-100 text-emerald-900"
                          : itemStatus === "rejected"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-dark-grey"
                  }`}
                >
                  {statusBadgeLabel}
                </span>
                {isDetailsView ? (
                  <span className="text-dark-grey/80 ml-2">Read-only</span>
                ) : null}
              </p>
              <p className="text-dark-grey text-base sm:text-lg mb-1 break-words">
                {reviewData.content_item.title}
              </p>
              <p className="text-dark-grey text-xs sm:text-sm">
                {(reviewData.content_item.content_type || "").toUpperCase()} · by{" "}
                {reviewData.content_item.author}
              </p>
              {itemStatus === "loaded" && reviewData.production?.loaded_post_id ? (
                <p className="text-dark-grey text-xs sm:text-sm mt-2 border-t border-dark-grey/10 pt-2">
                  <span className="font-semibold">Production post:</span>{" "}
                  {reviewData.production.loaded_post_id}
                  {typeof reviewData.production.public_site_visible === "boolean" ? (
                    <>
                      {" "}
                      ·{" "}
                      <span
                        className={
                          reviewData.production.public_site_visible
                            ? "text-emerald-800"
                            : "text-amber-800"
                        }
                      >
                        {reviewData.production.public_site_visible
                          ? "Visible on public site"
                          : "Hidden from public listings (approved_for_site is false)"}
                      </span>
                    </>
                  ) : null}
                  {reviewData.production.visibility_note ? (
                    <span className="block text-dark-grey/85 mt-1">
                      {reviewData.production.visibility_note}
                    </span>
                  ) : null}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {isDetailsView && itemStatus === "review_pending" ? (
                <Link to={`/pipeline/review/${contentId}`}>
                  <SimpleButton text="Open review" colour="green" />
                </Link>
              ) : null}
              {!isDetailsView && itemStatus !== "review_pending" && itemStatus !== "approved" ? (
                <Link to={`/pipeline/details/${contentId}`}>
                  <SimpleButton text="View details" colour="yellow" />
                </Link>
              ) : null}
              <SimpleButton
                text="Back"
                colour="yellow"
                onClick={() => navigate("/pipeline/dashboard")}
              />
            </div>
          </div>
        </Card>

        {showPublishActions ? (
          <Card colour="white" className="mb-5 border-2 border-green-200" paddingX="px-6 sm:px-8" paddingY="py-4 sm:py-5">
            <p className="text-dark-grey text-sm sm:text-base font-semibold mb-1">
              Approved — ready to publish
            </p>
            <p className="text-dark-grey/90 text-xs sm:text-sm mb-4">
              This item is approved but not yet on the live site. Publishing creates the production post
              (same as the dashboard “Publish” action).
            </p>
            <SimpleButton
              text="Publish to site"
              colour="green"
              onClick={() => setShowPublishConfirm(true)}
              className="w-full sm:w-auto"
            />
          </Card>
        ) : null}

        {/* Filtering Analysis */}
        {!hasFilteringData ? (
          <Card colour="white" className="mb-5" paddingX="px-6 sm:px-8" paddingY="py-5 sm:py-6">
            <h2 className="text-dark-grey text-xl sm:text-2xl font-bold mb-4">
              Filtering Analysis
            </h2>
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <p className="text-gray-600 text-base sm:text-lg mb-2">
                No filtering analysis available yet
              </p>
              <p className="text-gray-500 text-sm">
                Content must be processed before filtering results are available.
              </p>
            </div>
          </Card>
        ) : (
          <Card colour="white" className="mb-5" paddingX="px-6 sm:px-8" paddingY="py-5 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <h2 className="text-dark-grey text-xl sm:text-2xl font-bold">
                  Filtering Analysis
                </h2>
                {reviewData.filtering?.reviewed_result ? (
                  <span className="px-2.5 py-1 bg-orange/20 text-dark-grey text-xs font-semibold rounded-full whitespace-nowrap">
                    Reviewer overrides
                  </span>
                ) : null}
              </div>
              {showReviewActions ? (
                <div className="flex flex-col xs:flex-row gap-2 w-full sm:w-auto shrink-0">
                  {!filterEditMode ? (
                    <SimpleButton
                      text="Override context flags"
                      colour="yellow"
                      onClick={() => setFilterEditMode(true)}
                      className="w-full sm:w-auto"
                    />
                  ) : (
                    <>
                      <SimpleButton
                        text="Save overrides"
                        colour="orange"
                        onClick={handleSaveFiltering}
                        className="w-full sm:w-auto"
                      />
                      <SimpleButton
                        text="Cancel"
                        colour="yellow"
                        onClick={resetFilterEditFromData}
                        className="w-full sm:w-auto"
                      />
                    </>
                  )}
                </div>
              ) : null}
            </div>

            {showReviewActions && filterEditMode ? (
              <div className="mb-6 p-4 sm:p-5 rounded-2xl border border-gray-200 bg-white shadow-sm space-y-4">
                <p className="text-dark-grey text-sm leading-relaxed">
                  Toggle context flags if the automated result needs a correction. This saves a reviewed copy; the original model output stays on record. Sentiment and word-level sections below are unchanged from the model run.
                </p>
                {(
                  [
                    {
                      key: "generalization" as const,
                      label: "Generalization — flag if content overgeneralises groups or experiences",
                    },
                    {
                      key: "othering" as const,
                      label: "Othering — flag if content distances or marginalises groups",
                    },
                    {
                      key: "lack_of_direct_representation" as const,
                      label: "Lack of direct representation — flag if representation concerns apply",
                    },
                  ] as const
                ).map((item) => (
                  <label
                    key={item.key}
                    className="flex items-start gap-3 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-orange focus:ring-orange"
                      checked={filterFlags[item.key]}
                      onChange={(e) =>
                        setFilterFlags((prev) => ({
                          ...prev,
                          [item.key]: e.target.checked,
                        }))
                      }
                    />
                    <span className="text-dark-grey text-sm sm:text-base leading-snug">
                      {item.label}
                    </span>
                  </label>
                ))}
                <div>
                  <label
                    htmlFor="filtering-reviewer-notes"
                    className="block font-medium mb-2 text-sm sm:text-base text-dark-grey"
                  >
                    Notes (optional)
                  </label>
                  <TextArea
                    id="filtering-reviewer-notes"
                    value={filteringNotes}
                    onChange={(e) => setFilteringNotes(e.target.value)}
                    rows={3}
                    placeholder="Explain overrides for audit trail"
                  />
                </div>
              </div>
            ) : null}

            {/* Sentiment */}
            <div className="mb-6">
              <h3 className="font-bold text-base sm:text-lg mb-3">Sentiment Analysis</h3>
              <div className="flex flex-wrap gap-4 sm:gap-8 justify-center sm:justify-start">
                <div className="text-center min-w-[4rem]">
                  <p className="text-2xl sm:text-3xl font-bold text-green-600">
                    {(filteringAnalysis as any)?.sentiment_analysis?.analysis?.counts
                      ?.positive ?? 0}
                  </p>
                  <p className="text-xs sm:text-sm mt-1">Positive</p>
                </div>
                <div className="text-center min-w-[4rem]">
                  <p className="text-2xl sm:text-3xl font-bold text-yellow-600">
                    {(filteringAnalysis as any)?.sentiment_analysis?.analysis?.counts
                      ?.neutral ?? 0}
                  </p>
                  <p className="text-xs sm:text-sm mt-1">Neutral</p>
                </div>
                <div className="text-center min-w-[4rem]">
                  <p className="text-2xl sm:text-3xl font-bold text-red-600">
                    {(filteringAnalysis as any)?.sentiment_analysis?.analysis?.counts
                      ?.negative ?? 0}
                  </p>
                  <p className="text-xs sm:text-sm mt-1">Negative</p>
                </div>
              </div>
              {((filteringAnalysis as any)?.sentiment_analysis?.analysis?.negative_words_used
                ?.length ?? 0) > 0 && (
                <div className="mt-4">
                  <p className="font-medium text-sm sm:text-base">Negative words detected:</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(
                      (filteringAnalysis as any)?.sentiment_analysis?.analysis
                        ?.negative_words_used || []
                    ).map((word: string, i: number) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs sm:text-sm"
                      >
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Negative Words Analysis */}
            {(filteringAnalysis as any)?.negative_words_analysis?.analysis &&
              (filteringAnalysis as any).negative_words_analysis.analysis.length > 0 && (
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <h3 className="font-bold text-base sm:text-lg mb-3">
                    Flagged Words Analysis
                    {(filteringAnalysis as any).negative_words_analysis.trigger_flag === 1 && (
                      <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                        ⚠ Requires Review
                      </span>
                    )}
                  </h3>
                  <div className="space-y-4">
                    {(filteringAnalysis as any).negative_words_analysis.analysis.map(
                      (item: any, i: number) => (
                        <div
                          key={i}
                          className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-yellow-500 text-white font-bold text-sm">
                                {i + 1}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-yellow-900 mb-2">
                                Word: <span className="font-bold">&quot;{item.word}&quot;</span>
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3 text-xs">
                                <div>
                                  <span className="font-medium text-gray-600">Subject:</span>{" "}
                                  <span className="text-gray-900">{item.subject}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-600">Object:</span>{" "}
                                  <span className="text-gray-900">{item.object}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-600">Voice:</span>{" "}
                                  <span className="text-gray-900">{item.voice}</span>
                                </div>
                              </div>
                              <div className="bg-white p-3 rounded border border-yellow-200">
                                <p className="text-sm text-gray-700 italic">
                                  &quot;{item.text}&quot;
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Context Flags (read-only) */}
            {!filterEditMode ? (
              <div>
                <h3 className="font-bold text-base sm:text-lg mb-3">Context Analysis</h3>
                <div className="space-y-2">
                  {(
                    [
                      { key: "generalization", label: "Generalization" },
                      { key: "othering", label: "Othering" },
                      {
                        key: "lack_of_direct_representation",
                        label: "Lack of Direct Representation",
                      },
                    ] as const
                  ).map((item) => {
                    const ca = (filteringAnalysis as any)?.context_analysis?.analysis;
                    const value = ca?.[item.key];
                    return (
                      <div key={item.key} className="flex items-center gap-2 sm:gap-3 flex-wrap">
                        <span
                          className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium ${
                            value
                              ? "bg-red-100 text-red-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {value ? "⚠ FLAGGED" : "✓ PASSED"}
                        </span>
                        <span className="text-dark-grey text-xs sm:text-sm">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </Card>
        )}

        {/* Tags Section */}
        {!hasTaggingData ? (
          <Card colour="white" className="mb-5" paddingX="px-6 sm:px-8" paddingY="py-5 sm:py-6">
            <h2 className="text-dark-grey text-xl sm:text-2xl font-bold mb-4">Tags</h2>
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <p className="text-gray-600 text-base sm:text-lg mb-2">
                No tagging data available yet
              </p>
              <p className="text-gray-500 text-sm">
                Content must be processed before tags are generated.
              </p>
            </div>
          </Card>
        ) : (
          <Card colour="white" className="mb-5" paddingX="px-6 sm:px-8" paddingY="py-5 sm:py-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <h2 className="text-dark-grey text-xl sm:text-2xl font-bold">Tags</h2>
              {showReviewActions && !editMode && (
                <SimpleButton
                  text="Edit Tags"
                  colour="orange"
                  onClick={() => setEditMode(true)}
                />
              )}
            </div>

            {editMode ? (
              <div className="space-y-4">
                <div>
                  <label className="block font-medium mb-2 text-sm sm:text-base">Primary Tags</label>
                  <TextArea
                    id="primary-tags"
                    value={editedPrimaryTags.join(", ")}
                    onChange={(e) =>
                      setEditedPrimaryTags(
                        e.target.value.split(",").map((t: string) => t.trim()).filter(Boolean)
                      )
                    }
                    rows={3}
                    placeholder="Enter tags separated by commas"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-2 text-sm sm:text-base">Secondary Tags</label>
                  <TextArea
                    id="secondary-tags"
                    value={editedSecondaryTags.join(", ")}
                    onChange={(e) =>
                      setEditedSecondaryTags(
                        e.target.value.split(",").map((t: string) => t.trim()).filter(Boolean)
                      )
                    }
                    rows={3}
                    placeholder="Enter tags separated by commas"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-2 text-sm sm:text-base">Reviewer Notes</label>
                  <TextArea
                    id="reviewer-notes"
                    value={reviewerNotes}
                    onChange={(e) => setReviewerNotes(e.target.value)}
                    rows={3}
                    placeholder="Add notes about your changes"
                  />
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <SimpleButton text="Save Changes" colour="orange" onClick={handleSaveTags} />
                  <SimpleButton
                    text="Cancel"
                    colour="yellow"
                    onClick={() => setEditMode(false)}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2 text-sm sm:text-base">Primary Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {editedPrimaryTags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-2 sm:px-3 py-1 bg-orange text-white rounded-full text-xs sm:text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-medium mb-2 text-sm sm:text-base">Secondary Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {editedSecondaryTags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-2 sm:px-3 py-1 bg-yellow text-dark-grey rounded-full text-xs sm:text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Content Preview */}
        <Card colour="white" className="mb-5" paddingX="px-6 sm:px-8" paddingY="py-5 sm:py-6">
          <h2 className="text-dark-grey text-xl sm:text-2xl font-bold mb-3">
            Content Preview
          </h2>
          <div className="max-h-64 sm:max-h-96 overflow-y-auto rounded-2xl sm:rounded-3xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
            <div
              className="whitespace-pre-wrap break-words text-left text-sm sm:text-base leading-relaxed text-dark-grey font-sans"
              role="region"
              aria-label="Transcript preview"
            >
              {reviewData.content_item.transcript.substring(0, 2000)}
              {reviewData.content_item.transcript.length > 2000 && "…"}
            </div>
          </div>
        </Card>

        {/* Review decisions: only on review route while pending */}
        {!isDetailsView &&
        mode === "review" &&
        itemStatus === "review_pending" &&
        hasFilteringData &&
        hasTaggingData ? (
          <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
            <SimpleButton
              text="Reject"
              colour="red"
              onClick={() => setShowRejectModal(true)}
              className="w-full sm:w-auto"
            />
            <SimpleButton
              text="Approve"
              colour="green"
              onClick={() => setShowApproveConfirm(true)}
              className="w-full sm:w-auto"
            />
          </div>
        ) : null}

        {!isDetailsView &&
        mode === "review" &&
        itemStatus === "review_pending" &&
        (!hasFilteringData || !hasTaggingData) ? (
          <Card colour="yellow" paddingX="px-6 sm:px-8" paddingY="py-5 sm:py-6">
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-yellow border-4 border-orange mb-4">
                <ExclamationTriangleIcon className="w-8 h-8 text-orange" />
              </div>
              <p className="text-dark-grey text-lg sm:text-xl font-bold mb-2">
                Content Not Ready for Review
              </p>
              <p className="text-dark-grey text-sm sm:text-base max-w-md">
                This content is in &quot;
                <span className="font-bold">{reviewData.content_item.status}</span>&quot; status. It must be
                processed before you can approve or reject it.
              </p>
              <div className="mt-5">
                <SimpleButton
                  text="Back to Dashboard"
                  colour="orange"
                  onClick={() => navigate("/pipeline/dashboard")}
                />
              </div>
            </div>
          </Card>
        ) : null}

        {isDetailsView &&
        itemStatus === "review_pending" &&
        (!hasFilteringData || !hasTaggingData) ? (
          <Card colour="white" paddingX="px-6 sm:px-8" paddingY="py-5 sm:py-6" className="border border-gray-200">
            <p className="text-dark-grey text-sm sm:text-base text-center">
              Processing is not complete yet. Open <strong>Review</strong> once tagging and filtering results are
              available.
            </p>
          </Card>
        ) : null}
      </Container>

      {/* Modals */}
      <AlertModal
        show={alertModal.show}
        setShow={(show) => setAlertModal({ ...alertModal, show })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />

      <ConfirmModal
        show={showApproveConfirm}
        setShow={setShowApproveConfirm}
        title="Approve content"
        message="Approve this item for publication? You can publish to the live site afterwards (this step only saves the review decision)."
        onConfirm={handleApprove}
        confirmText="Approve"
        type="warning"
      />

      <ConfirmModal
        show={showPublishConfirm}
        setShow={setShowPublishConfirm}
        title="Publish to site"
        message="Create the production post now? This step is idempotent if the item was already loaded."
        onConfirm={handlePublish}
        confirmText="Publish"
        type="warning"
      />

      <RejectModal
        show={showRejectModal}
        setShow={setShowRejectModal}
        onConfirm={handleReject}
      />
    </Template>
  );
}
